<template>
    <div>
        <div 
            class="modal fade" 
            id="moreDetailsGenericModal" 
            tabindex="-1" 
            aria-labelledby="moreDetailsGenericModalLabel" 
            aria-hidden="true"
            >
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="moreDetailsGenericModalLabel">{{ title }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div>
                            <table class="table more-details-table">
                                <tbody>
                                    <tr v-for="(value, key) in normalizedMoreDetails" :key="key">
                                        <td class="more-details-table__key"><strong>{{ formatKey(key) }}: </strong></td>
                                        <td class="more-details-table__value">
                                            <div
                                                v-if="key === 'description' && typeof value === 'string'"
                                                class="more-details-description"
                                                v-html="descriptionHtml(value)"
                                            ></div>
                                            <ModalDetailsValueRenderer v-else :value="value" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { descriptionHtml, formatMarginValue, formatMoneyValue, humanizeKey } from '@/helpers/string.js'
import { formatComponentControlDate } from '@/helpers/dates.js'
import ModalDetailsValueRenderer from './ModalDetailsValueRenderer.vue'

const MONEY_DETAIL_FIELDS = new Set([
    'unit_cost',
    'total_cost',
    'unit_price',
    'total_price',
    'profit',
    'quote',
    'ext_quote',
    'price',
    'list_price',
    'your_price',
    'est_cost',
])

const DATE_DETAIL_FIELDS = new Set([
    'time_created',
    'time_updated',
    'post_date',
    'entry_date',
    'next_ship_date',
    'next_delivery_date',
])

export default {
    name: 'ModalMoreDetailsGeneric',
    components: {
        ModalDetailsValueRenderer,
    },
    props: {
        moreDetails: {
            type: Object,
            default: () => {}
        },
        title: {
            type: String,
            default: 'More Details'
        }
    },
    computed: {
        normalizedMoreDetails() {
            if (!this.moreDetails || typeof this.moreDetails !== 'object') {
                return {}
            }

            const normalized = {}
            for (const [key, value] of Object.entries(this.moreDetails)) {
                normalized[key] = this.normalizeValue(value, key)
            }
            return normalized
        },
    },
    methods: {
        descriptionHtml,
        formatKey(key) {
            if (key == 'customer_pn') {
                return 'Customer P/N'
            }
            else if (key == 'original_po_number') {
                return 'Original PO Number'
            }


            return humanizeKey(key)
        },
        isPlainObject(value) {
            return value !== null && typeof value === 'object' && !Array.isArray(value)
        },
        isProbablyJsonString(value) {
            if (typeof value !== 'string') {
                return false
            }

            const trimmed = value.trim()
            if (!trimmed) {
                return false
            }

            const isObjectShape = trimmed.startsWith('{') && trimmed.endsWith('}')
            const isArrayShape = trimmed.startsWith('[') && trimmed.endsWith(']')
            return isObjectShape || isArrayShape
        },
        parseJsonObjectOrArray(value) {
            if (!this.isProbablyJsonString(value)) {
                return value
            }

            try {
                const parsed = JSON.parse(value)
                if (parsed !== null && typeof parsed === 'object') {
                    return parsed
                }
                return value
            } catch (error) {
                return value
            }
        },
        formatKnownValue(value, key) {
            if (MONEY_DETAIL_FIELDS.has(key)) {
                return formatMoneyValue(value)
            }
            if (key === 'margin' || key === 'discount') {
                return formatMarginValue(value)
            }
            if (DATE_DETAIL_FIELDS.has(key)) {
                return formatComponentControlDate(value)
            }
            return value
        },
        normalizeValue(value, key = null) {
            if (value === null || value === undefined) {
                return null
            }

            if (typeof value === 'string') {
                const trimmed = value.trim()
                if (trimmed === '') {
                    return null
                }

                const parsed = this.parseJsonObjectOrArray(trimmed)
                if (parsed !== trimmed) {
                    return this.normalizeValue(parsed, key)
                }
                return this.formatKnownValue(value, key)
            }

            if (Array.isArray(value)) {
                return value.map((entry) => this.normalizeValue(entry, key))
            }

            if (this.isPlainObject(value)) {
                const normalizedObject = {}
                for (const [entryKey, entryValue] of Object.entries(value)) {
                    normalizedObject[entryKey] = this.normalizeValue(entryValue, entryKey)
                }
                return normalizedObject
            }

            return this.formatKnownValue(value, key)
        },
    }
}
</script>

<style scoped>
    .more-details-description {
        max-height: 30rem;
        overflow: auto;
        overflow-wrap: anywhere;
        text-align: left;
    }

    .more-details-description :deep(table) {
        width: 100%;
        table-layout: fixed;
    }

    html:not([data-theme='dark']) .more-details-description,
    html:not([data-theme='dark']) .more-details-description :deep(*) {
        color: #000;
    }

    .more-details-table {
        margin-bottom: 0;
    }

    .more-details-table__key {
        width: 34%;
        font-weight: 700;
        color: var(--color-text-primary);
    }

    .more-details-table__key,
    .more-details-table__value {
        vertical-align: top;
    }

    .more-details-table__key strong {
        color: var(--color-text-primary) !important;
    }
    
    html[data-theme='dark'] .more-details-table__key {
        background: var(--color-surface-canvas) !important;
        color: var(--color-text-inverse) !important;
        border-color: var(--color-border-muted) !important;
    }

    html[data-theme='dark'] .more-details-table__key strong {
        color: var(--color-text-inverse) !important;
    }

    html[data-theme='dark'] .more-details-table__value {
        background: var(--color-surface-canvas) !important;
        color: var(--color-text-primary) !important;
        border-color: var(--color-border-muted) !important;
    }
</style>
